import React from "react";
import { observer } from "mobx-react";
import { Grid, Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { GroupContainer } from "../../../../common/components";
import { FormToolbox } from "../../../../common/controls/forms/FormToolbox";
import { FormSaveFooter } from "../../../../common/controls/forms/FormSaveFooter";

import FormExtras from "../../../../common/components/formExtras";
import { EsignatureConfigCreateStore } from "../../store";
import { EsignatureProvider } from "../../../../api/graphql";
import { InputReference } from "../../../common/controls";
import { InputCodeEditor } from "../../../common/controls";
import { InputBoolean } from "../../../common/controls";
import { InputString } from "../../../common/controls";
import { FormControl } from "@mui/material";

export type EsignatureConfigCreateFormProps = {
  onSave?: () => void;
  onCancel?: () => void;
  /** Mientras se guarda, Guardar queda deshabilitado. */
  saving?: boolean;
  showToolbox?: boolean;
  showSaveFooter?: boolean;
  providerError?: string;
  providerOptions: { id: string; displayId: string }[];
  provider: EsignatureProvider;
  onProviderChange?: (value: EsignatureProvider) => void;
  credentialsSelectedValue?: { displayId: string; id: string } | null;
  onCredentialsChange?: (value: string) => void;
  credentials: string;
  credentialsError?: string;
  sandboxModeSelectedValue?: { displayId: string; id: string } | null;
  onSandboxModeChange?: (value: boolean) => void;
  sandboxMode: boolean;
  sandboxModeError?: string;
  webhookSecretSelectedValue?: { displayId: string; id: string } | null;
  onWebhookSecretChange?: (value: string) => void;
  webhookSecret: string;
  webhookSecretError?: string;
  enabledSelectedValue?: { displayId: string; id: string } | null;
  onEnabledChange?: (value: boolean) => void;
  enabled: boolean;
  enabledError?: string;
  esignatureConfigCreateStore: EsignatureConfigCreateStore;
};

export const EsignatureConfigCreateForm: React.FC<EsignatureConfigCreateFormProps> =
  observer(
    ({
      onSave = () => {},
      onCancel = () => {},
      saving = false,
      showToolbox = true,
      showSaveFooter,
      providerError,
      providerOptions = [],
      provider = "" as EsignatureProvider,
      onProviderChange = () => {},
      credentials = "{}",
      credentialsError,
      onCredentialsChange = () => {},
      sandboxMode = false,
      sandboxModeError,
      onSandboxModeChange = () => {},
      webhookSecret = "",
      webhookSecretError,
      onWebhookSecretChange = () => {},
      enabled = false,
      enabledError,
      onEnabledChange = () => {},
      esignatureConfigCreateStore,
    }) => {
      const rootStore = useRootStore();
      const { langStore } = rootStore;

      return (
        <Grid>
          {showToolbox && (
            <FormToolbox
              saveAndCloseLabel={langStore.saveEntity}
              closeLabel={langStore.cancelNewEntity}
              saveForm={() => onSave()}
              saveDisabled={saving}
              closeForm={() => onCancel()}
            />
          )}
          <Box>
            <FormExtras
              entity="EsignatureConfig"
              mode="create"
              values={{}}
              options={{}}
              onPatch={(campo: string, valor: any) => {
                switch (campo) {
                  case "provider":
                    onProviderChange?.(valor);
                    break;
                  case "credentials":
                    onCredentialsChange?.(valor);
                    break;
                  case "sandboxMode":
                    onSandboxModeChange?.(valor);
                    break;
                  case "webhookSecret":
                    onWebhookSecretChange?.(valor);
                    break;
                  case "enabled":
                    onEnabledChange?.(valor);
                    break;
                }
              }}
            />
            <GroupContainer
              id="null"
              errorText={esignatureConfigCreateStore.getCardErrors("null")}
              title={langStore.groupEsignatureConfigLabel}
              collapsible={false}
            >
              <FormControl fullWidth>
                <InputReference
                  id="provider"
                  autoFocus
                  error={Boolean(providerError)}
                  helperText={
                    providerError ||
                    esignatureConfigCreateStore.warnings["provider"]?.message
                  }
                  label={langStore.EsignatureConfigProviderLabel}
                  options={providerOptions}
                  selectedValue={
                    provider
                      ? {
                          id: provider,
                          displayId:
                            providerOptions.find(
                              (o: { id: string }) => o.id === provider
                            )?.displayId || provider,
                        }
                      : null
                  }
                  onChange={() => {}}
                  onSelect={(value: { id: string; displayId: string } | null) =>
                    onProviderChange(
                      (value?.id as EsignatureProvider) ||
                        ("" as EsignatureProvider)
                    )
                  }
                />

                <InputCodeEditor
                  value={credentials}
                  error={Boolean(credentialsError)}
                  helperText={
                    credentialsError ||
                    esignatureConfigCreateStore.warnings["credentials"]?.message
                  }
                  label={langStore.EsignatureConfigCredentialsLabel}
                  onChange={(value: string) => onCredentialsChange(value)}
                  language="json"
                  description={langStore.EsignatureConfigCredentialsDescription}
                />
                <InputBoolean
                  value={sandboxMode}
                  error={Boolean(sandboxModeError)}
                  helperText={
                    sandboxModeError ||
                    esignatureConfigCreateStore.warnings["sandboxMode"]?.message
                  }
                  label={langStore.EsignatureConfigSandboxModeLabel}
                  onChange={(value: boolean) => onSandboxModeChange(value)}
                />

                <InputString
                  value={webhookSecret}
                  error={Boolean(webhookSecretError)}
                  warning={
                    !webhookSecretError &&
                    Boolean(
                      esignatureConfigCreateStore.warnings["webhookSecret"]
                    )
                  }
                  helperText={
                    webhookSecretError ||
                    esignatureConfigCreateStore.warnings["webhookSecret"]
                      ?.message
                  }
                  label={langStore.EsignatureConfigWebhookSecretLabel}
                  onChange={(value: string) => onWebhookSecretChange(value)}
                  nullText={langStore.optionalFieldHint}
                />
                <InputBoolean
                  value={enabled}
                  error={Boolean(enabledError)}
                  helperText={
                    enabledError ||
                    esignatureConfigCreateStore.warnings["enabled"]?.message
                  }
                  label={langStore.EsignatureConfigEnabledLabel}
                  onChange={(value: boolean) => onEnabledChange(value)}
                />
              </FormControl>
            </GroupContainer>
          </Box>
          {(showSaveFooter ?? !showToolbox) && (
            <FormSaveFooter
              onSave={() => onSave()}
              label={langStore.saveEntity}
              disabled={saving}
            />
          )}
        </Grid>
      );
    }
  );
